import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Smartphone, ShieldCheck, Fuel, ArrowRight, Play, Chrome } from "lucide-react";
import AppFooter from "@/components/AppFooter";

import { useTranslation } from "react-i18next";
/** Public download page for the Android APK. Reads latest version from
 * /api/version/latest so dealers always get the newest signed APK.
 */
export default function DownloadPage() {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/version/latest").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const apkUrl = meta?.apk_url || "https://github.com/dst3858/HisabX/releases/latest";
  const latest = meta?.latest || "1.0.2";
  const isApkComingSoon = meta && meta.apk_available === false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-background to-background dark:from-amber-500/5">
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="download-back-home">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Fuel className="h-4 w-4" />
            </div>
            <span className="font-display text-base font-bold tracking-tight">{t("downloadpage.hisabx")}</span>
          </Link>
          <Link to="/login" className="text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="download-login">
            Sign in →
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:py-16">
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              <ShieldCheck className="h-3.5 w-3.5" /> Signed release · Latest v{latest}
            </div>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Get HisabX on your pump's Android phone
            </h1>
            <p className="mt-3 text-base text-muted-foreground">
              Direct APK install — no Play Store required. Every drop counted, every rupee tracked.
              Works offline for reads &amp; drafts, syncs when you're back online.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              {isApkComingSoon ? (
                <div className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-amber-500/15 px-6 text-sm font-bold text-amber-800 dark:text-amber-300 ring-1 ring-amber-500/40">
                  <Download className="h-4 w-4" /> APK coming very soon
                </div>
              ) : (
                <a
                  href={apkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition hover:-translate-y-0.5 hover:shadow-xl"
                  data-testid="download-apk-btn"
                >
                  <Download className="h-4 w-4" /> Download APK (v{latest})
                </a>
              )}
              <Link
                to="/login"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border-2 border-primary/40 bg-primary/5 px-6 text-sm font-bold text-primary transition hover:bg-primary/10"
                data-testid="download-web-btn"
              >
                <Chrome className="h-4 w-4" /> Use the web app now →
              </Link>
              <a
                href="https://play.google.com/store/apps/details?id=in.hisabx.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border-2 border-border bg-background px-6 text-sm font-bold text-foreground transition hover:border-primary/40"
                data-testid="download-play-btn"
              >
                <Play className="h-4 w-4" /> Play Store <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white">{t("downloadpage.soon")}</span>
              </a>
            </div>
            {isApkComingSoon && (
              <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
                <div className="mb-1 font-semibold">📱 APK ready in a few days — use the web app for now.</div>
                Full features work perfectly in Chrome/Safari on any Android or iPhone. Add HisabX to your home screen for a native-app feel. We'll notify you the moment the signed APK is live.
              </div>
            )}
            {meta?.notes && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                <div className="font-semibold">What's new in v{latest}</div>
                <div className="mt-0.5 whitespace-pre-wrap">{meta.notes}</div>
              </div>
            )}
          </div>

          <Card className="border-border bg-background/60 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Smartphone className="h-4 w-4 text-primary" /> Install in 3 steps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Step n={1} title={t("downloadpage.download_the_apk")}>
                Tap the "Download APK" button. Your browser saves it to <b>{t("downloadpage.downloads")}</b>.
              </Step>
              <Step n={2} title={t("downloadpage.allow_install_from_unknown_sources")}>
                First time only: Android will ask permission. Tap <b>Settings → allow this source</b>.
              </Step>
              <Step n={3} title={t("downloadpage.open_the_apk_to_install")}>
                Tap the file, then <b>{t("downloadpage.install")}</b>. Done — HisabX icon appears on your home screen.
              </Step>
              <div className="mt-4 rounded-md bg-muted/40 p-3 text-[11px] text-muted-foreground">
                <b>{t("downloadpage.system_requirements")}</b> Android 7.0 (Nougat) or newer · ~40 MB free space · Camera + GPS for photos & attendance
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-10 grid gap-3 rounded-2xl border border-border/60 bg-background/40 p-5 sm:grid-cols-3">
          <FeatureRow icon={<ShieldCheck className="h-4 w-4" />} title={t("downloadpage.signed_safe")} desc="Every release is signed with our production keystore." />
          <FeatureRow icon={<Chrome className="h-4 w-4" />} title={t("downloadpage.or_use_the_web")} desc="Full app also works in Chrome/Safari on any device." link="/login" />
          <FeatureRow icon={<ArrowRight className="h-4 w-4" />} title={t("downloadpage.trouble_installing")} desc="Email support@hisabx.in — we'll help in <2 hours." />
        </div>
      </main>

      <AppFooter />
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">{n}</div>
      <div className="flex-1">
        <div className="font-semibold text-foreground">{title}</div>
        <div className="text-xs text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, title, desc, link }) {
  const body = (
    <>
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">{icon}{title}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
    </>
  );
  if (link) return <Link to={link} className="block rounded-md p-2 transition hover:bg-muted/40" data-testid={`feature-${title.replace(/\W+/g, '-').toLowerCase()}`}>{body}</Link>;
  return <div className="p-2">{body}</div>;
}
